import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { ProfileCard_delegate } from './__generated__/ProfileCard_delegate.graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdBadge,
  faEnvelope,
  faBriefcase,
  faPhone
} from '@fortawesome/pro-duotone-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  personalInfo: {},
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    marginBottom: '1rem'
  },
  infoItems: {
    display: 'grid',
    gap: '2rem',
    gridTemplateColumns: '1fr 1fr'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  infoItem__iconWrap: {
    flexShrink: 0,
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: '#eef3ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  infoItem__icon: {
    fontSize: '0.95rem',
    color: '#0b57ff'
  },
  infoItem__body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    minWidth: 0
  },
  infoItem__label: {
    color: '#6A728F',
    margin: 0,
    padding: 0,
    fontSize: '0.8rem'
  },
  infoItem__text: {
    margin: 0,
    padding: 0,
    fontSize: '0.9rem'
  },
  subtitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    margin: 0
  }
}));

type Props = {
  delegate: ProfileCard_delegate;
  action?: React.ReactNode;
};

function ProfileCard({ delegate, action }: Props) {
  const classes = useStyles();

  return (
    <div className={classes.personalInfo}>
      <div className={classes.header}>
        <p className={classes.subtitle}>Personal Information</p>
        {action}
      </div>
      <div className={classes.infoItems}>
        <div className={classes.infoItem}>
          <span className={classes.infoItem__iconWrap}>
            <FontAwesomeIcon
              icon={faIdBadge}
              className={classes.infoItem__icon}
            />
          </span>
          <div className={classes.infoItem__body}>
            <p className={classes.infoItem__label}>TTC login</p>
            <p className={classes.infoItem__text}>{delegate.TTC_ID}</p>
          </div>
        </div>
        <div className={classes.infoItem}>
          <span className={classes.infoItem__iconWrap}>
            <FontAwesomeIcon
              icon={faEnvelope}
              className={classes.infoItem__icon}
            />
          </span>
          <div className={classes.infoItem__body}>
            <p className={classes.infoItem__label}>Email</p>
            <p className={classes.infoItem__text}>
              {delegate.email ? delegate.email : '-'}
            </p>
          </div>
        </div>
        <div className={classes.infoItem}>
          <span className={classes.infoItem__iconWrap}>
            <FontAwesomeIcon
              icon={faBriefcase}
              className={classes.infoItem__icon}
            />
          </span>
          <div className={classes.infoItem__body}>
            <p className={classes.infoItem__label}>Job title</p>
            <p className={classes.infoItem__text}>
              {delegate.jobTitle ? delegate.jobTitle : '-'}
            </p>
          </div>
        </div>
        {delegate.telephone && (
          <div className={classes.infoItem}>
            <span className={classes.infoItem__iconWrap}>
              <FontAwesomeIcon
                icon={faPhone}
                className={classes.infoItem__icon}
              />
            </span>
            <div className={classes.infoItem__body}>
              <p className={classes.infoItem__label}>Phone</p>
              <p className={classes.infoItem__text}>{delegate.telephone}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ProfileCardFrag = createFragmentContainer(ProfileCard, {
  delegate: graphql`
    fragment ProfileCard_delegate on Delegate {
      email
      TTC_ID
      jobTitle
      telephone
    }
  `
});

export default ProfileCardFrag;
